// Shared reverse-geocoding helper (OpenStreetMap Nominatim, no API key required).
// Cache + in-flight dedupe keyed by "lat,lng" so repeated clicks on the same
// spot (or re-renders) don't re-hit the API.
const detailsCache = new Map();
const detailsInFlight = new Map();

export const reverseGeocode = (lat, lng) => {
  const key = `${lat},${lng}`;

  if (detailsCache.has(key)) return Promise.resolve(detailsCache.get(key));
  if (detailsInFlight.has(key)) return detailsInFlight.get(key);

  const promise = fetch(
    `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=16&addressdetails=1`,
    { headers: { "Accept-Language": "en" } }
  )
    .then((res) => res.json())
    .then((data) => {
      const address = data?.address || {};
      const result = {
        locationName:
          address.office ||
          address.building ||
          address.amenity ||
          address.road ||
          address.suburb ||
          address.city ||
          address.town ||
          address.village ||
          data?.display_name?.split(",").slice(0, 2).join(", ") ||
          "",
        country: address.country || "",
        countryCode: address.country_code ? address.country_code.toUpperCase() : "",
      };
      detailsCache.set(key, result);
      detailsInFlight.delete(key);
      return result;
    })
    .catch((err) => {
      detailsInFlight.delete(key);
      throw err;
    });

  detailsInFlight.set(key, promise);
  return promise;
};

// Shared forward-geocoding helper (OpenStreetMap Nominatim, no API key
// required). Lets a user type a place name and get back candidate
// coordinates, keyed/cached by the trimmed query text.
const searchCache = new Map();

export const searchLocations = (query) => {
  const trimmed = query.trim();
  if (!trimmed) return Promise.resolve([]);
  if (searchCache.has(trimmed)) return Promise.resolve(searchCache.get(trimmed));

  return fetch(
    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
      trimmed
    )}&format=json&addressdetails=1&limit=5`,
    { headers: { "Accept-Language": "en" } }
  )
    .then((res) => res.json())
    .then((data) => {
      const results = (data || [])
        .map((item) => {
          const address = item.address || {};
          const lat = parseFloat(item.lat);
          const lng = parseFloat(item.lon);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return {
            lat,
            lng,
            displayName: item.display_name,
            locationName:
              address.office ||
              address.building ||
              address.amenity ||
              address.road ||
              address.suburb ||
              address.city ||
              address.town ||
              address.village ||
              item.display_name?.split(",").slice(0, 2).join(", ") ||
              "",
            country: address.country || "",
          };
        })
        .filter(Boolean);
      searchCache.set(trimmed, results);
      return results;
    })
    .catch(() => []);
};

// Matches a reverse-geocoded country name against the tenant-specific list of
// configured country/company options (case-insensitive), since those Select
// fields only allow values from that restricted list, not any world country.
export const matchCountryName = (countryName, countryOptions = []) => {
  if (!countryName) return null;
  const normalized = countryName.trim().toLowerCase();
  const match = countryOptions.find(
    (option) => option && option.trim().toLowerCase() === normalized
  );
  return match || null;
};
